import CheckMark from '@allesauto/design/graphics/checked.svg';
import clsx from 'clsx';
import React, { useCallback } from 'react';

import { StepIndicatorProps } from '..';
import ConditionalWrapper from '../../ConditionalWrapper';
import NextLink from '../../nextLink';

export interface StepItemProps extends React.PropsWithChildren<any> {
  disabled?: boolean;
  label: string;
  itemIndex?: number;
  currentStep?: StepIndicatorProps['currentStep'];
  className?: string;
  isSmallBreakpoint?: boolean;
  isDropdownDisplay?: boolean;
  link?: {
    href: string;
    title?: string;
  };
  onClick?: (itemIndex: number) => void;
  showCurrentStepAsDone?: boolean;
  disableNavigation?: boolean;
}

const StepItem = ({
  label,
  itemIndex = 0,
  currentStep = 0,
  disabled,
  isSmallBreakpoint,
  isDropdownDisplay,
  className: customClassName,
  link,
  onClick,
  showCurrentStepAsDone,
  disableNavigation,
  ...restProps
}: StepItemProps): JSX.Element => {
  const isDone = useCallback(() => {
    return (
      itemIndex < currentStep ||
      (itemIndex === currentStep && !!showCurrentStepAsDone)
    );
  }, [currentStep, itemIndex, showCurrentStepAsDone]);

  const isCurrent = useCallback(() => {
    return itemIndex === currentStep;
  }, [currentStep, itemIndex]);

  const isNext = useCallback(() => {
    return itemIndex > currentStep;
  }, [currentStep, itemIndex]);

  const className = {
    listItem:
      'flex row text-purple80 monochrome:text-black relative md:pl-0 md:pr-0 sm:p-15px items-center',
    ...((disabled || isNext()) && {
      listItemDisabled: 'text-grey60 monochrome:text-grey60',
    }),
    listItemBefore:
      'before:absolute before:block after:content-[""] before:h-[3px] before:bg-green70 monochrome:before:bg-black before:bottom-0 before:left-0 before:z-10',
    ...(!isDone() && !isCurrent() && { listItemBeforeStateNone: 'before:w-0' }),
    ...(isDone() && !disabled && { listItemBeforeStateDone: 'before:w-full' }),
    ...(isDone() && disabled && { listItemBeforeStateNone: 'before:w-full' }),
    ...(isCurrent() &&
      !disabled && { listItemBeforeStateCurrent: 'before:w-1/2' }),
    listItemAfter:
      'after:absolute after:content-[""] after:block after:right-0 after:bg-grey30 after:bottom-0 after:left-0 after:z-1',
    ...(!isSmallBreakpoint && { listItemAfterHeight: 'after:h-[3px]' }),
    ...(isSmallBreakpoint && {
      listItemAfterHeightSmallBreakpoint: 'after:h-[1px] last:after:hidden',
    }),
    indicatorDone:
      'w-[14px] h-[14px] bg-green70 monochrome:bg-black !text-white overflow-hidden flex grow-0 shrink-0 justify-center items-center rounded-[14px]',
    link: 'border-b-1px border-b-transparent cursor-pointer duration-300 ease-in-out h-20px inline-block text-purple80 transition-all monochrome:hover:border-b-grey90 monochrome:hover:text-grey90 monochrome:text-grey90 hover:border-b-1px hover:border-b-purple80 hover:text-purple80',
  };

  return (
    <li
      {...restProps}
      className={clsx(
        className.listItem,
        className.listItemDisabled,
        !isDropdownDisplay && !isSmallBreakpoint && className.listItemBefore,
        !isDropdownDisplay && className.listItemAfter,
        !isDropdownDisplay && className.listItemAfterHeight,
        !isDropdownDisplay && className.listItemAfterHeightSmallBreakpoint,
        !isDropdownDisplay &&
          !isSmallBreakpoint &&
          className.listItemBeforeStateNone,
        !isDropdownDisplay &&
          !isSmallBreakpoint &&
          className.listItemBeforeStateCurrent,
        !isDropdownDisplay &&
          !isSmallBreakpoint &&
          className.listItemBeforeStateDone,
        isSmallBreakpoint && 'p-[15px] after:!left-[15px] after:!right-[15px]',
        customClassName,
        'wl-progress-bar-color'
      )}
      title={label}
    >
      {!isDropdownDisplay && (
        <span
          className={clsx(
            isDone() && !disabled ? className.indicatorDone : undefined,
            'mr-10px',
            (!isDone() || disabled) && 'wl-color',
            isDone() && !disabled && 'wl-background-color'
          )}
        >
          {(!isDone() || disabled) && `${itemIndex + 1}.`}
          {isDone() && !disabled && <CheckMark width={7} height={6} />}
        </span>
      )}

      <ConditionalWrapper
        condition={
          !!(link || onClick) && isDone() && !disabled && !disableNavigation
        }
        wrapper={(children) => (
          <>
            {link && (
              <NextLink href={link.href ?? '#'} className={className.link}>
                {children}
              </NextLink>
            )}
            {onClick && (
              <span
                className={className.link}
                onClick={() => onClick(itemIndex)}
              >
                {children}
              </span>
            )}
          </>
        )}
      >
        <>
          {label && (
            <span className="inline-block text-current text-ellipsis wl-color">
              {label}
            </span>
          )}
        </>
      </ConditionalWrapper>
    </li>
  );
};

export default StepItem;
